import type { LiveSession } from "@/lib/odds-screen/session";
import {
  coerceDismissedBetEventStartMs,
  type DismissedBetMetadata,
} from "@/lib/dismissal-fingerprints";
import { clampedImpliedProbabilityToAmericanOdds } from "@/lib/odds";
import { normalizeSportsbookId } from "@/lib/sportsbook-registry";
import { isOverSlotSide, isUnderSlotSide } from "@/lib/selection-side";
import { gameTimeValue } from "@/lib/game-date";
import type { DisplayPlayerProp, PlatformType } from "./utils-v2";
import type {
  OptimizerQueryState,
  OptimizerStickyFilterScope,
  SortDirection,
} from "./query-state";
import type { InitialOptimizerStickyHydration } from "@/lib/optimizer-server-state";
import { OPTIMIZER_COMPARISON_ORDER, getComparisonBookRankMap } from "./comparison-book-order";
import type { UserWeightPreset } from "@/lib/actions/weight-presets";
import type { SlipData, ComparisonOdd } from "@/lib/slip/types";
import type { TableVariant } from "./utils-v2";
import { getSportsbookSurfacePolicy, SPORTSBOOK_SURFACE, type SportsbookSurfaceId } from "@/lib/sportsbook-preferences";
import type { OptimizerComparisonOrderRuntime } from "./comparison-order-dialog";
import type { MobileView } from "@/lib/saved-filter-pages";
import type { ColumnPreference } from "@/components/fantasy-props/model";

const FILTER_KEY_MAP: Partial<Record<keyof OptimizerQueryState, string>> = {
  sportsbook: "sportsbook",
  leagues: "league",
  statTypes: "stat_type",
  matchups: "matchup",
  marketScope: "market_type",
  playerSearch: "player_search",
  minEV: "min_ev",
  minDataPoints: "min_books",
  minLiquidity: "min_liquidity",
  minOddsToHit: "min_odds_to_hit",
  maxOddsToHit: "max_odds_to_hit",
  minOdds: "min_odds",
  maxOdds: "max_odds",
  requiredComparisonBooks: "required_book",
  weights: "weights",
  requiredBooks: "required_books",
  hidePushProps: "hide_push_props",
  overUnder: "over_under",
  slipType: "slip_type",
};

export type FilterChange = { readonly filter_name: string; readonly filter_value: string };

export function filterChanges(partial: Partial<OptimizerQueryState>, next: OptimizerQueryState): FilterChange[] {
  const changes: FilterChange[] = [];
  for (const key of Object.keys(partial) as (keyof OptimizerQueryState)[]) {
    const filterName = FILTER_KEY_MAP[key];
    if (!filterName) continue;
    const raw = partial[key];
    changes.push({ filter_name: filterName, filter_value: Array.isArray(raw) ? raw.join(",") : String(raw ?? "") });
  }
  if (partial.gameDays !== undefined || partial.startsWithin !== undefined || partial.live !== undefined) {
    changes.push({ filter_name: "date", filter_value: gameTimeValue({ days: next.gameDays, within: next.startsWithin, live: next.live }) });
  }
  return changes;
}

function extractOpponentFromEventName(eventName: string | null | undefined, teamName: string): string {
  if (!eventName) return "";

  const vsMatch = eventName.match(/^(.+?)\s+vs\.?\s+(.+)$/i);
  if (vsMatch) {
    const [, team1, team2] = vsMatch;
    const normalizedTeam = teamName.toUpperCase().trim();
    const normalizedTeam1 = team1.toUpperCase().trim();
    const normalizedTeam2 = team2.toUpperCase().trim();

    if (normalizedTeam === normalizedTeam1) {
      return team2.trim();
    } else if (normalizedTeam === normalizedTeam2) {
      return team1.trim();
    }
    return team2.trim();
  }

  return "";
}

function normalizeLineValue(
  line: DisplayPlayerProp["line"] | string | null | undefined,
): number | undefined {
  if (line == null || line === "") return undefined;
  const numeric = typeof line === "number" ? line : Number(line);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function buildComparisonOdds(
  comparisonOddsRaw: DisplayPlayerProp["__comparison_odds_raw"],
  primaryBook: string | null | undefined,
  betType: string,
  analyticalBookIds: readonly PlatformType[],
  primaryLine?: number,
  comparisonPriorityOrder: readonly PlatformType[] = OPTIMIZER_COMPARISON_ORDER,
): ComparisonOdd[] {
  if (!comparisonOddsRaw) return [];

  const entriesByBook = new Map<string, ComparisonOdd>();
  const normalizedPrimary = primaryBook
    ? normalizeSportsbookId(primaryBook) || primaryBook.toLowerCase()
    : "";
  const normalizedBetType = betType.toUpperCase();

  for (const [bookId, entry] of Object.entries(comparisonOddsRaw)) {
    const knownBookId = normalizeSportsbookId(bookId);
    if (knownBookId && !analyticalBookIds.includes(knownBookId)) continue;
    const canonicalBookId = knownBookId || bookId.toLowerCase();
    if (canonicalBookId === normalizedPrimary) continue;

    if (entry.american_odds == null) continue;

    if (entry.selection_side) {
      const entrySelectionSide = entry.selection_side.toUpperCase();
      const matchesSide =
        entrySelectionSide === normalizedBetType ||
        (isOverSlotSide(normalizedBetType) && isOverSlotSide(entrySelectionSide)) ||
        (isUnderSlotSide(normalizedBetType) && isUnderSlotSide(entrySelectionSide));
      if (!matchesSide) continue;
    }

    const entryLine = entry.line != null ? entry.line : undefined;
    const lineDiffers = primaryLine !== undefined && entryLine !== undefined && entryLine !== primaryLine;

    if (entriesByBook.has(canonicalBookId)) continue;
    entriesByBook.set(canonicalBookId, {
      bookId: canonicalBookId,
      odds: entry.american_odds,
      ...(lineDiffers && { line: entryLine }),
    });
  }

  const entries = [...entriesByBook.values()];
  const comparisonBookRank = getComparisonBookRankMap(comparisonPriorityOrder);
  entries.sort((a, b) => {
    const aPriority = comparisonBookRank[a.bookId.toLowerCase()] ?? 999;
    const bPriority = comparisonBookRank[b.bookId.toLowerCase()] ?? 999;
    return aPriority - bPriority;
  });

  return entries.slice(0, 4);
}

export function getOptimizerDismissalMetadata(row: DisplayPlayerProp, tableVariant: TableVariant | "optimizer"): DismissedBetMetadata {
  const eventStart = coerceDismissedBetEventStartMs(row.game_start_timestamp);

  return {
    tool: tableVariant === "props" ? "Props Optimizer" : tableVariant === "sportsbook" ? "Sportsbook Props" : "Optimizer",
    sport: row.sport ?? undefined,
    league: row.league ?? row.sport ?? undefined,
    eventId: row.game_id ?? undefined,
    eventName: row.event_name ?? undefined,
    entityId: row.player_id ?? row.player_name ?? row.event_name ?? undefined,
    entityName: row.player_name ?? row.selection_label ?? row.event_name ?? undefined,
    market: row.stat_type ?? row.market_display ?? undefined,
    line: row.line ?? null,
    side: row.bet_type ?? row.selection_side ?? undefined,
    selection: [row.selection_label ?? row.player_name ?? row.event_name, row.bet_type ?? row.selection_side, row.line]
      .filter((value) => value !== undefined && value !== null && value !== "")
      .join(" · "),
    sportsbook: row.primary_book ?? undefined,
    ...(eventStart !== undefined ? { eventStart } : {}),
  };
}

export function getOptimizerDismissalTracking(row: DisplayPlayerProp, tableVariant: TableVariant | "optimizer") {
  return {
    page: "optimizer" as const,
    source: tableVariant === "props" ? "optimizer_props" : tableVariant === "sportsbook" ? "optimizer_sportsbook" : "optimizer",
    sport: row.sport ?? undefined,
    league: row.league ?? row.sport ?? undefined,
    event_id: row.game_id ?? undefined,
    entity_id: row.player_name ?? row.event_name ?? undefined,
    market: row.stat_type ?? row.market_display ?? undefined,
    line: row.line ?? null,
    side: row.bet_type ?? row.selection_side ?? undefined,
    table_variant: tableVariant,
    sportsbook: row.primary_book ?? undefined,
  };
}

export function propToSlipData(
  prop: DisplayPlayerProp,
  isGameLine = true,
  comparisonPriorityOrder: readonly PlatformType[] = OPTIMIZER_COMPARISON_ORDER,
): SlipData {
  const teamName = prop.team || "";
  const opponentName = extractOpponentFromEventName(prop.event_name, teamName);

  const oddsToHitPercent = typeof prop.odds_to_hit === "number" ? prop.odds_to_hit : Number(prop.odds_to_hit) || 50;
  const fairOdds = clampedImpliedProbabilityToAmericanOdds(oddsToHitPercent / 100);

  const primaryLine = normalizeLineValue(prop.line);
  const comparisonOdds = buildComparisonOdds(
    prop.__comparison_odds_raw,
    prop.primary_book,
    prop.bet_type,
    getSportsbookSurfacePolicy(SPORTSBOOK_SURFACE.OPTIMIZER).analyticalBookIds,
    primaryLine,
    comparisonPriorityOrder,
  );

  return {
    playerName: prop.player_name || prop.event_name || "Unknown",
    statType: prop.stat_type || prop.market_display || "",
    line: normalizeLineValue(prop.line) ?? 0,
    betType: (prop.bet_type as SlipData["betType"]) || "OVER",
    evPercentage: prop.ev_percentage ?? null,
    oddsToHit: oddsToHitPercent,
    league: (prop.league || prop.sport || "").toString().toUpperCase(),
    eventName: prop.event_name || "",
    gameDate: prop.game_start_time || "",
    isGameLine,
    selectionLabel: prop.selection_label || undefined,
    sportsbook: prop.primary_book || undefined,
    odds: typeof prop.primary_odds === "number" ? prop.primary_odds : undefined,
    teamName,
    opponentName,
    fairOdds,
    comparisonOdds,
  };
}

export type LiveOptimizerPageProps = OptimizerPageClientProps & { readonly comparisonColumns: ColumnPreference };

export interface OptimizerSportsbookPreferenceRuntime {
  effectiveBookId: PlatformType;
  saveSelection(bookId: PlatformType): Promise<boolean>;
}

export interface OptimizerPageClientProps {
  initialState: OptimizerQueryState;
  initialSort: { column: string; direction: SortDirection };
  tutorial?: { videoUrl: string; toolLabel: string; toolId?: string };
  betLinkNudgeDismissed?: boolean;
  allowedSportsbooks?: readonly PlatformType[];
  myBooks?: readonly PlatformType[];
  defaultBookIds?: readonly PlatformType[];
  comparisonPriorityOrder?: readonly PlatformType[];
  showWeightsFilter?: boolean;
  initialUserId?: string;
  stickyFilterScope?: OptimizerStickyFilterScope;
  initialStickyHydration?: InitialOptimizerStickyHydration;
  sportsbookSurface?: SportsbookSurfaceId;
  sportsbookPreferencesConfigured?: boolean;
  initialSportsbookFromExplicitUrl?: boolean;
  sportsbookPreferenceRuntime?: OptimizerSportsbookPreferenceRuntime;
  comparisonOrderRuntime?: OptimizerComparisonOrderRuntime;
  initialUserPresets?: UserWeightPreset[] | null;
  initialLiveSession?: LiveSession | null;
  initialMobileView?: MobileView;
  diamond: boolean;
}
